import React from 'react';

import CheckCircleIcon from '@atlaskit/icon/core/check-circle';

import {
  Table,
  createColumnWidths,
  createColumnsWidths,
} from '../../atoms/Table';
import {
  CheckCircleIconStyled,
  TableStyled,
} from './UnfinishedTestCaseExecutions.styled';
import UnfinishedTestCaseExecutionsSkeletonRow from './UnfinishedTestCaseExecutionsSkeletonRow';
import UnfinishedTestCaseExecutionsTableRow, {
  UnfinishedTestCaseExecutionsTableRowProps,
} from './UnfinishedTestCaseExecutionsTableRow';

type Columns =
  | 'testCase'
  | 'testCycle'
  | 'created'
  | 'executionStatus'
  | 'assignee'
  | 'action';

export const ColumnsWidths = createColumnsWidths<Columns>({
  testCase: createColumnWidths('400px', '200px', '700px'),
  testCycle: createColumnWidths('225px', '100px', '400px'),
  created: createColumnWidths('90px', '90px', '120px'),
  executionStatus: createColumnWidths('120px', '120px', '160px'),
  assignee: createColumnWidths('60px', '60px', '60px'),
  action: createColumnWidths('120px', '80px', '120px'),
});

export type Props = {
  unfinishedTestCaseExecutionsData: Array<UnfinishedTestCaseExecutionsTableRowProps>;
  isLoading: boolean;
};

export default function UnfinishedTestCaseExecutionsTable({
  unfinishedTestCaseExecutionsData,
  isLoading,
}: Readonly<Props>) {
  const isTableDataEmpty =
    !isLoading && unfinishedTestCaseExecutionsData.length == 0;

  return (
    <TableStyled displayBottomBorder>
      <Table data-testid='UnfinishedTestCaseExecutions-Table' useFixedLayout>
        <Table.Head>
          <Table.Tr>
            <Table.Th widths={ColumnsWidths.testCase}>Test case</Table.Th>
            <Table.Th widths={ColumnsWidths.testCycle}>Test cycle</Table.Th>
            <Table.Th widths={ColumnsWidths.created}>Created</Table.Th>
            <Table.Th widths={ColumnsWidths.executionStatus}>
              Execution status
            </Table.Th>
            <Table.Th widths={ColumnsWidths.assignee}>Assignee</Table.Th>
            <Table.Th widths={ColumnsWidths.action}>Action</Table.Th>
          </Table.Tr>
        </Table.Head>
        <Table.Body>
          {isTableDataEmpty && (
            <Table.Tr>
              <Table.Td colSpan={6} rowSpan={6}>
                <Table.ContentHelper
                  alignItems='center'
                  justifyContent='center'
                >
                  <CheckCircleIconStyled>
                    <CheckCircleIcon label='All test case executions are finished.' />
                    <span>All test case executions are finished.</span>
                  </CheckCircleIconStyled>
                </Table.ContentHelper>
              </Table.Td>
            </Table.Tr>
          )}
          {isLoading &&
            Array.from({ length: 10 }).map((_, index) => (
              <UnfinishedTestCaseExecutionsSkeletonRow
                key={'unfinished-test-case-executions-skeleton-row-' + index}
              />
            ))}
          {!isTableDataEmpty &&
            !isLoading &&
            unfinishedTestCaseExecutionsData.map(
              (
                unfinishedTestCaseExecutionRow: UnfinishedTestCaseExecutionsTableRowProps,
                index: number
              ) => {
                return (
                  <UnfinishedTestCaseExecutionsTableRow
                    key={'unfinished-test-case-execution-row-' + index}
                    testExecutionUrl={
                      unfinishedTestCaseExecutionRow.testExecutionUrl
                    }
                    status={unfinishedTestCaseExecutionRow.status}
                    createdDate={unfinishedTestCaseExecutionRow.createdDate}
                    assignee={unfinishedTestCaseExecutionRow.assignee}
                    testCase={unfinishedTestCaseExecutionRow.testCase}
                    testCycle={unfinishedTestCaseExecutionRow.testCycle}
                    testTypeProps={unfinishedTestCaseExecutionRow.testTypeProps}
                  />
                );
              }
            )}
        </Table.Body>
      </Table>
    </TableStyled>
  );
}
